import {
  toolCallResultSchema,
  ToolCallResultStatus,
} from 'common/src/modules/creativeTemplates/copilot/copilotCreativeTemplates.consts';
import {
  complexTextDefaultMinFontSize,
  complexTextVersion,
} from 'common/src/modules/complexText/complexText.consts';
import {
  ComplexTextAlign,
  ComplexTextSizeUnit,
  ComplexTextStyle,
} from 'common/src/modules/complexText/complexText.enums';
import {
  RemoteComplexTextLine,
  RemoteTextPart,
} from 'common/src/modules/complexText/complexText.types';
import {DPMappingVerticalAlign} from 'common/src/modules/dynamicParameters/dynamicParameters.enums';
import {TemplateAsset} from 'common/src/modules/dynamicParameters/dynamicParameters.types';
import {MovieAlign} from 'common/src/modules/movie/movie.enums';
import {TextLayerText} from 'common/src/modules/movie/movieLayers.types';
import appendCustomCallerToCallChain from 'common/src/modules/shared/copilot/utils/creators/appendCustomCallerToCallChain';
import openAISystemMessage from 'common/src/modules/shared/copilot/openAI/utils/openAISystemMessage';
import openAIUserMessage from 'common/src/modules/shared/copilot/openAI/utils/openAIUserMessage';
import {Caller, ToolUtils} from 'common/src/modules/shared/copilot/copilot.types';
import {z} from 'zod';
import {textLineSchema} from '../adBuilderAgent/adBuilderAgent.schemas';

// Schema for LLM text content generation
const textContentSchema = z.object({
  verticalAlign: z.enum(['top', 'middle', 'bottom']).nullable().optional(),
  lines: z.array(textLineSchema),
  boxTextWidth: z.number().nullable().optional(),
  boxTextHeight: z.number().nullable().optional(),
});

export interface TextContentResult {
  aeSrcTxt: TextLayerText['aeSrcTxt'];
  isModification?: boolean;
  unsupportedRequest?: string;
}

/**
 * Helper function to extract existing text content to schema format
 */
function extractTextToSchema(
  aeSrcTxt: TextLayerText['aeSrcTxt'],
): z.infer<typeof textContentSchema> {
  const lines: z.infer<typeof textLineSchema>[] = [];

  if (aeSrcTxt.txtObj?.lines) {
    // Extract from complex text object
    lines.push(
      ...aeSrcTxt.txtObj.lines.map((line) => ({
        parts: line.parts.map((part) => ({
          text: part.text,
          color:
            part.color && Array.isArray(part.color)
              ? `#${Math.round(part.color[0] * 255)
                  .toString(16)
                  .padStart(2, '0')}${Math.round(part.color[1] * 255)
                  .toString(16)
                  .padStart(2, '0')}${Math.round(part.color[2] * 255)
                  .toString(16)
                  .padStart(2, '0')}`
              : undefined,
          size: part.size,
          family: part.family,
          letterSpacing: part.letterSpacing,
          strikeThrough: part.strikeThrough,
          underline: part.underline,
          x_shift: part.x_shift,
          y_shift: part.y_shift,
        })),
        align: (line.align === ComplexTextAlign.Left
          ? 'left'
          : line.align === ComplexTextAlign.Center
            ? 'center'
            : line.align === ComplexTextAlign.Right
              ? 'right'
              : 'original') as 'left' | 'center' | 'right' | 'original',
        lead: line.lead,
      })),
    );
  } else {
    // Extract from simple text
    const textLines = aeSrcTxt.txt.split('\n');
    const color = aeSrcTxt.fColArr
      ? `#${Math.round(aeSrcTxt.fColArr[0] * 255)
          .toString(16)
          .padStart(2, '0')}${Math.round(aeSrcTxt.fColArr[1] * 255)
          .toString(16)
          .padStart(2, '0')}${Math.round(aeSrcTxt.fColArr[2] * 255)
          .toString(16)
          .padStart(2, '0')}`
      : undefined;

    lines.push(
      ...textLines.map((line) => ({
        parts: [
          {
            text: line,
            color,
            size: aeSrcTxt.size,
            family: aeSrcTxt.fontFamily,
            letterSpacing: aeSrcTxt.trk ? (aeSrcTxt.trk / 1000) * aeSrcTxt.size : 0,
            strikeThrough: aeSrcTxt.strikeThrough || 0,
            underline: aeSrcTxt.underline || 0,
            x_shift: 0,
            y_shift: 0,
          },
        ],
        align: 'left' as const,
        lead: aeSrcTxt.lead || 1.2,
      })),
    );
  }

  return {
    verticalAlign:
      aeSrcTxt.verticalAlign === DPMappingVerticalAlign.Center
        ? 'middle'
        : aeSrcTxt.verticalAlign === DPMappingVerticalAlign.Bottom
          ? 'bottom'
          : 'top',
    lines,
    boxTextWidth: aeSrcTxt.boxTxtS?.w,
    boxTextHeight: aeSrcTxt.boxTxtS?.h,
  };
}

export default async function getTextContentFromPrompt({
  contentDescription,
  width,
  height,
  fontMetadata,
  existingAeSrcTxt,
  toolUtils,
  callChain,
}: {
  contentDescription: string;
  width: number | null;
  height: number | null;
  fontMetadata: TemplateAsset['metadata'];
  existingAeSrcTxt: TextLayerText['aeSrcTxt'] | undefined;
  toolUtils: ToolUtils;
  callChain: Caller[];
}): Promise<TextContentResult> {
  const isModification = !!existingAeSrcTxt;

  // Extract current text context for modifications
  let currentTextContext = '';

  if (isModification && existingAeSrcTxt) {
    const textContext = extractTextToSchema(existingAeSrcTxt);
    currentTextContext = JSON.stringify(textContext, null, 2);
  }

  // Build context-aware system prompt
  const systemPrompt = isModification
    ? `You are a text content modifier. You modify existing text content based on natural language descriptions.

Current text context:
${currentTextContext}

When modifying text:
- Preserve existing properties and Only modify the properties mentioned in the user's request.

- verticalAlign refers to the whole text lines inside the text box
- Each line can have multiple parts with different styling
- Colors should be in hex format (e.g. #FF0000)`
    : `You are a text content generator helping to convert natural language descriptions into structured text content.

- verticalAlign refers to the whole text lines inside the text box
- Each line can have multiple parts with different styling
- Colors should be in hex format (e.g., #FF0000)`;

  // Build context-aware user prompt
  const sizeConstraint =
    width !== null && height !== null
      ? `\nThis text is planned to be contained in a ${width}x${height}px text box but it is not necessary to cover the full text box.`
      : '';

  const userPrompt = isModification
    ? `Modify the existing text content based on: ${contentDescription}.
    
Return the complete text configuration with both modified and unchanged properties.${sizeConstraint}`
    : `Generate text content for this description: ${contentDescription}${sizeConstraint}`;

  // Use LLM to generate text content
  const response = await toolUtils.invokeStandaloneLLMCall({
    messages: [openAISystemMessage(systemPrompt), openAIUserMessage(userPrompt)],
    responseSchema: toolCallResultSchema.extend({
      result: textContentSchema,
    }),
    buildResponseUsingToolCall: true,
    callChain: appendCustomCallerToCallChain('getTextContentFromPrompt', callChain),
  });

  // Check if LLM indicated failure
  if (response.status === ToolCallResultStatus.Failure) {
    const reason = response.failureReason || 'Request failed without specific reason';
    throw new Error(`Text generation failed: ${reason}`);
  }

  const textContent = response.result;

  // Convert generated content to complex text lines
  const complexLines: RemoteComplexTextLine[] = textContent.lines.map((line) => ({
    align: line.align
      ? line.align === 'left'
        ? ComplexTextAlign.Left
        : line.align === 'center'
          ? ComplexTextAlign.Center
          : line.align === 'right'
            ? ComplexTextAlign.Right
            : ComplexTextAlign.Original
      : ComplexTextAlign.Left,
    lead: line.lead || 1,
    parts: line.parts.map(
      (part): RemoteTextPart => ({
        text: part.text,
        color: part.color || '#000000',
        size: part.size || 16,
        sizeUnits: ComplexTextSizeUnit.AbsolutePixels,
        style: ComplexTextStyle.Normal,
        weight: 'normal',
        letterSpacing: part.letterSpacing || 0,
        strikeThrough: part.strikeThrough || 0,
        underline: part.underline || 0,
        x_shift: part.x_shift || 0,
        y_shift: part.y_shift || 0,
        family: part.family || fontMetadata.family || '',
        url: part.url || fontMetadata.url || '',
      }),
    ),
  }));

  // Generate full text string
  const fullText = complexLines
    .map((line) => line.parts.map((part) => part.text).join(''))
    .join('\n');

  // Create the aeSrcTxt object
  const aeSrcTxt: TextLayerText['aeSrcTxt'] = {
    txt: fullText,
    size: complexLines[0]?.parts[0]?.size || 16,
    boxTxtS: {
      w: textContent.boxTextWidth || width || 400,
      h: textContent.boxTextHeight || height || 100,
    },
    txtObj: {
      lines: complexLines,
      verticalAlign:
        textContent.verticalAlign === 'middle'
          ? DPMappingVerticalAlign.Center
          : textContent.verticalAlign === 'bottom'
            ? DPMappingVerticalAlign.Bottom
            : DPMappingVerticalAlign.Top,
      allCaps: false,
      wrapText: true,
      minFontSize: complexTextDefaultMinFontSize,
      fonts: [],
      ver: complexTextVersion,
    },
    verticalAlign:
      textContent.verticalAlign === 'middle'
        ? DPMappingVerticalAlign.Center
        : textContent.verticalAlign === 'bottom'
          ? DPMappingVerticalAlign.Bottom
          : DPMappingVerticalAlign.Top,
    fontFileName: fontMetadata.name || '',
    fontFamily: fontMetadata.family || 'Arial',
    fontStyle: 'normal',
    justif: MovieAlign.Left,
    blShift: 0,
    sWidth: 0,
    fColArr: [0, 0, 0], // Default black color
    sOverF: false,
    trk: 0,
    lead: complexLines[0]?.lead || 1.2,
    allCaps: false,
    smallCaps: false,
    subScr: false,
    supScr: false,
    tsume: 0,
    vScale: 1,
    hScale: 1,
    weight: 'normal',
    strikeThrough: 0,
    underline: 0,
    minFontSize: complexTextDefaultMinFontSize,
    applyFill: true,
    italic: false,
    boxTxtP: existingAeSrcTxt?.boxTxtP || {x: 0, y: 0},
  };

  // Set color based on first part
  const firstPart = complexLines[0]?.parts[0];

  if (firstPart?.color) {
    const hex = firstPart.color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;

    aeSrcTxt.fColArr = [r, g, b];
  }

  return {
    aeSrcTxt,
    isModification,
    ...(response.unsupportedRequestPart && {unsupportedRequest: response.unsupportedRequestPart}),
  };
}
